import React, { useState, useEffect } from 'react';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Label } from '../components/ui/Label';
import apiClient from '../services/apiClient';
import toast from 'react-hot-toast';
import { Cog6ToothIcon, BellIcon, ShieldCheckIcon, KeyIcon } from '@heroicons/react/24/outline';

const AdminSystemSettings = () => {
  const [settings, setSettings] = useState({
    license_expiry_warning_days_1: '30',
    license_expiry_warning_days_2: '7',
    smtp_host: '',
    smtp_port: '',
    smtp_user: '',
    admin_email: ''
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Set page title
  useEffect(() => {
    document.title = 'System Settings | Admin Portal';
  }, []);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const response = await apiClient.get('/admin/system-settings');
      if (response.settings) {
        const settingsObj = {};
        response.settings.forEach(setting => {
          settingsObj[setting.key] = setting.value;
        });
        setSettings(prev => ({ ...prev, ...settingsObj }));
      }
    } catch (error) {
      console.error('Error loading settings:', error);
      toast.error('Failed to load system settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (category) => {
    setSaving(true);
    try {
      let updates = [];
      
      if (category === 'license') {
        updates = [
          { key: 'license_expiry_warning_days_1', value: settings.license_expiry_warning_days_1, description: 'First license expiration warning (days before)', category: 'LICENSE' },
          { key: 'license_expiry_warning_days_2', value: settings.license_expiry_warning_days_2, description: 'Second license expiration warning (days before)', category: 'LICENSE' }
        ];
      } else if (category === 'email') {
        updates = [
          { key: 'smtp_host', value: settings.smtp_host, description: 'SMTP server hostname', category: 'EMAIL' },
          { key: 'smtp_port', value: settings.smtp_port, description: 'SMTP server port', category: 'EMAIL' },
          { key: 'smtp_user', value: settings.smtp_user, description: 'SMTP username/email', category: 'EMAIL' },
          { key: 'admin_email', value: settings.admin_email, description: 'Admin email for notifications', category: 'EMAIL' }
        ];
      }

      await apiClient.post('/admin/system-settings', { settings: updates });
      toast.success('Settings saved successfully');
      loadSettings(); // Reload to get updated values
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error(error.response?.data?.error || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#197560]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="border-b border-gray-200 pb-4">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center">
          <Cog6ToothIcon className="w-8 h-8 mr-3 text-[#197560]" />
          System Settings
        </h1>
        <p className="text-sm text-gray-600 mt-1">
          Configure system-wide settings for the XLicense portal
        </p>
      </div>

      {/* License Expiration Settings */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center mb-4">
          <BellIcon className="w-6 h-6 mr-2 text-orange-500" />
          <h2 className="text-lg font-semibold text-gray-900">License Expiration Notifications</h2>
        </div>
        <p className="text-sm text-gray-600 mb-6">
          Configure when customers receive email warnings before their licenses expire. The system checks daily at 9:00 AM.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <Label htmlFor="warning_days_1">First Warning (days before expiry)</Label>
            <Input
              id="warning_days_1"
              type="number"
              min="1"
              max="365"
              value={settings.license_expiry_warning_days_1}
              onChange={(e) => setSettings({ ...settings, license_expiry_warning_days_1: e.target.value })}
              placeholder="30"
            />
            <p className="text-xs text-gray-500 mt-1">
              Default: 30 days. Sends first warning email.
            </p>
          </div>

          <div>
            <Label htmlFor="warning_days_2">Second Warning (days before expiry)</Label>
            <Input
              id="warning_days_2"
              type="number"
              min="1"
              max="365"
              value={settings.license_expiry_warning_days_2}
              onChange={(e) => setSettings({ ...settings, license_expiry_warning_days_2: e.target.value })}
              placeholder="7"
            />
            <p className="text-xs text-gray-500 mt-1">
              Default: 7 days. Sends urgent warning email.
            </p>
          </div>
        </div>

        <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex">
            <div className="flex-shrink-0">
              <ShieldCheckIcon className="h-5 w-5 text-blue-600" />
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-blue-800">How it works</h3>
              <div className="mt-2 text-sm text-blue-700">
                <ul className="list-disc list-inside space-y-1">
                  <li>Cron job runs daily at 9:00 AM server time</li>
                  <li>Checks all active licenses for expiration dates</li>
                  <li>Sends email if license expires in exactly N days (±1 day window)</li>
                  <li>Each customer receives one email per warning threshold</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-6 flex justify-end">
          <Button
            onClick={() => handleSave('license')}
            disabled={saving}
          >
            {saving ? 'Saving...' : 'Save License Settings'}
          </Button>
        </div>
      </div>

      {/* Email Settings */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center mb-4">
          <KeyIcon className="w-6 h-6 mr-2 text-blue-500" />
          <h2 className="text-lg font-semibold text-gray-900">Email Configuration</h2>
        </div>
        <p className="text-sm text-gray-600 mb-6">
          Configure SMTP settings for sending email notifications. Changes require backend restart.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <Label htmlFor="smtp_host">SMTP Host</Label>
            <Input
              id="smtp_host"
              type="text"
              value={settings.smtp_host}
              onChange={(e) => setSettings({ ...settings, smtp_host: e.target.value })}
              placeholder="smtp.office365.com"
            />
          </div>

          <div>
            <Label htmlFor="smtp_port">SMTP Port</Label>
            <Input
              id="smtp_port"
              type="number"
              value={settings.smtp_port}
              onChange={(e) => setSettings({ ...settings, smtp_port: e.target.value })}
              placeholder="587"
            />
          </div>

          <div>
            <Label htmlFor="smtp_user">SMTP User</Label>
            <Input
              id="smtp_user"
              type="email"
              value={settings.smtp_user}
              onChange={(e) => setSettings({ ...settings, smtp_user: e.target.value })}
              placeholder="noreply@xloud.tech"
            />
          </div>

          <div>
            <Label htmlFor="admin_email">Admin Email</Label>
            <Input
              id="admin_email"
              type="email"
              value={settings.admin_email}
              onChange={(e) => setSettings({ ...settings, admin_email: e.target.value })}
              placeholder="admin@xloud.tech"
            />
          </div>
        </div>

        <div className="mt-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-yellow-600" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-yellow-800">Note</h3>
              <p className="mt-1 text-sm text-yellow-700">
                Email settings are stored in the database but require backend environment variables to be updated and the server to be restarted for changes to take effect.
              </p>
            </div>
          </div>
        </div>

        <div className="mt-6 flex justify-end">
          <Button
            onClick={() => handleSave('email')}
            disabled={saving}
            variant="outline"
          >
            {saving ? 'Saving...' : 'Save Email Settings'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AdminSystemSettings;
